<template>
  <div
    class="procedures-search-bar"
    :class="{ 'procedures-search-bar--with-container-bg': withContainerBackground }"
    data-testid="procedures-search-bar"
  >
    <div class="procedures-search-bar__input-wrap">
      <!-- Figma text input container (253514): fill 696px, hug 44px, gap spacing-md -->
      <div class="procedures-search-bar__text-input-container">
        <Icon name="interface_search" size="small" class="procedures-search-bar__icon" />
        <input
          ref="inputRef"
          type="text"
          class="procedures-search-bar__input"
          :value="currentValue"
          :placeholder="placeholder"
          :aria-label="placeholder"
          data-testid="procedures-search-input"
          @input="onInput"
        />
        <button
          v-if="currentValue && clearButtonLabel"
          type="button"
          class="procedures-search-bar__clear"
          :aria-label="clearButtonLabel"
          @click="clear"
        >
          {{ clearButtonLabel }}
          <Icon
            name="interface_close"
            size="extra-small"
            class="procedures-search-bar__clear-icon"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import Icon from "@components/icons/Icon.vue";

const props = defineProps({
  /** Vue 2 v-model compatibility (prop: value, event: input) */
  value: {
    type: String,
    default: undefined,
  },
  modelValue: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Search in procedures",
  },
  clearButtonLabel: {
    type: String,
    default: "Clear search",
  },
  /** When true, applies the Figma purple outer container layout (#53389E, 1540×130px, padding, radius, gap) */
  withContainerBackground: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "input", "search"]);

const inputRef = ref(null);

const currentValue = computed(() => {
  // Prefer Vue 3-style modelValue when it is explicitly provided; otherwise fall back to Vue 2-style value
  if (props.modelValue !== "" || props.value === undefined || props.value === null) {
    return props.modelValue;
  }
  return props.value;
});

function onInput(event) {
  const value = event.target ? event.target.value : "";
  emit("update:modelValue", value);
  emit("input", value);
  emit("search", value);
}

function clear() {
  emit("update:modelValue", "");
  emit("input", "");
  emit("search", "");
  if (inputRef.value) {
    inputRef.value.focus();
  }
}
</script>

<style scoped>
/* Base: no container background */
.procedures-search-bar {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
}

.procedures-search-bar:not(.procedures-search-bar--with-container-bg) {
  min-height: 44px;
}

/* Figma: purple outer container – flow vertical, 1540×130px, radius 6px top, padding 43px 398px, gap 10px, #53389E */
.procedures-search-bar--with-container-bg {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: none;
  height: 130px;
  padding: 43px 48px;
  gap: 10px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  background-color: #53389e;
  box-sizing: border-box;
}

/* Figma: internal container (253513) – fill 744px, hug 44px, padding L/R spacing-3xl (24px), radius-md 8px, shadow-xs */
.procedures-search-bar__input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: none;
  min-height: 44px;
  height: 44px;
  padding: 0 24px 0 24px; /* spacing-3xl left & right */
  border-radius: 8px; /* radius-md */
  border: 1px solid #d0d5dd; /* border-primary */
  background-color: #ffffff; /* bg-primary */
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05); /* shadow-xs */
  box-sizing: border-box;
}

.procedures-search-bar--with-container-bg .procedures-search-bar__input-wrap {
  max-width: 1211px;
  margin: 0 auto;
}

/* Figma: text input container (253514) – fill 696px, hug 44px, gap spacing-md (8px), flow vertical in parent */
.procedures-search-bar__text-input-container {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: none;
  min-height: 44px;
  height: 44px;
  gap: 8px; /* spacing-md */
  position: relative;
  box-sizing: border-box;
}

/* Icon color: fg-quarterary / text-placeholder */
.procedures-search-bar__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #667085;
  pointer-events: none;
}

/* Input: Text md/Regular (16px/24px) */
.procedures-search-bar__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0; /* Gap is now handled by container */
  border: none;
  outline: none;
  background: transparent;
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #344054;
}

.procedures-search-bar__input::placeholder {
  color: #667085; /* text-placeholder */
}

.procedures-search-bar__input::-webkit-search-cancel-button {
  display: none;
}

.procedures-search-bar__clear {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 0 4px;
  border: none;
  background: transparent;
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #667085;
  cursor: pointer;
}

.procedures-search-bar__clear:hover {
  color: #344054;
}

.procedures-search-bar__clear-icon {
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .procedures-search-bar--with-container-bg {
    height: auto;
    padding: 24px;
  }
}

@media (max-width: 600px) {
  .procedures-search-bar--with-container-bg {
    padding: 16px;
  }
}
</style>
